import { formatDigitOnly } from './FormatUtils'
// status is default value
export const emptyStringValidation = (str: string, errorMessage: string, status: boolean) => {
    const result = str === null || str === '' || str.trim() === '' ? errorMessage : ''
    status = result === '' || status
    return result
}

const onlyNumberRegex = /^[0-9]+$/
const alphaNumericRegex = /^[0-9a-zA-Z]+$/
// 3000-303F : punctuation
// 3040-309F : hiragana
// 30A0-30FF : katakana
// FF00-FFEF : Full-width roman + half-width katakana
// 4E00-9FAF : Common and uncommon kanji
//
// Non-Japanese punctuation/formatting characters commonly used in Japanese text
// 2605-2606 : Stars
// 2190-2195 : Arrows
// u203B     : Weird asterisk thing
const japaneseCharPattern = '\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF'
const numericPattern = '0-9'
const alphaNumericPattern = '0-9a-zA-Z'

/*
this method is currently ignore, it was implemented to use with number input.
But according to new requirement, number plates are only validate on backend.
 */
export const numberPlateValidator = (str: string, errorMessage: string) => {
  if (isOnlyContainerSpecialCharacter(str)) {
    return errorMessage
  }

  str = purgeStr(str)
  const pattern = new RegExp(`^[0-9]{0,3}([${japaneseCharPattern}]{2,3}|)[0-9a-zA-Z]{0,3}([${japaneseCharPattern}]?)[0-9]{0,4}$`)
  if (!pattern.test(str)) {
     return errorMessage
  }
  // for alphanumeric input that is longer than 3, last four character must be digit
  if (str.length > 3 && createPattern(alphaNumericPattern).test(str)) {
    return /^[0-9A-Za-z]*[0-9]{4}$/.test(str) ? '' : errorMessage
  }
  return ''
}

function isOnlyContainerSpecialCharacter (str: string) {
  return /^[-\s.]*$/.test(str)
}

function purgeStr (str: string) {
  return str.trim().replace(/[. -]/g, '')
}

function createPattern (str: string) {
  return new RegExp(`^[${str}]+$`)
}

export const phoneNumberValidation = (str: string, errorMessage: string, status: boolean) => {
    const result = emptyStringValidation(str, errorMessage, status)
    if (result !== '') {
        return result
    }
    return !str.match('[0-9-]+') ? errorMessage : ''
}

export const purifyTimeFormat = (str: string) => {
    // to adjust user input 'x:--' => '0x:--' for consistent validation
    if (str.split(':')[0].length === 1) {
        str = '0' + str
    }

    str = formatDigitOnly(str)
    if (str.length === 2) {
        return str + ':00'
    }

    if (str.length === 3) {
        return str.substring(0, 2) + ':0' + str.substring(2)
    }

    if (str.length === 4) {
        return str.substring(0, 2) + ':' + str.substring(2, 4)
    }
    return str
}

export const timeStringValidator = (str: string, errorMessage: string, status: boolean) => {
    const isEmpty = emptyStringValidation(str, errorMessage + '.empty', status)
    if (isEmpty !== '') {
        return isEmpty
    }
    // format check xx:xx
    if (!str.match('[0-9]{2}:[0-9]{2}')) {
        return errorMessage + '.format'
    }
    // hour check
    const hourStr = str.split(':')[0]
    if (hourStr.length > 2) {
        return errorMessage + '.format'
    }
    if (!hourStr.match('([01]{1}?[0-9]{1}|2[0-3])')) {
        return errorMessage + '.hour'
    }

    // min check
    const minStr = str.split(':')[1]
    if (minStr.length > 2) {
        return errorMessage + '.format'
    }
    return !minStr.match('[0-5]{1}?[0-9]{1}') ? errorMessage + '.min' : ''
}

/**
 * Auto-converts inputs like '1111' -> '11:11', '111' -> '11:01', '9' -> '09:00'
 *
 * @param input - Raw input string from the user
 * @returns Formatted time string (HH:mm) or empty string
 */
export const formatAccidentTime = (input: string | null | undefined): string => {
  if (!input) return ''

  // Strip all non-digit characters
  const digits = input.replace(/\D/g, '')

  if (!digits) return ''

  let hours = '00'
  let minutes = '00'

  if (digits.length === 1) {
    hours = digits.padStart(2, '0')
  } else if (digits.length === 2) {
    hours = digits
  } else if (digits.length === 3) {
    hours = digits.substring(0, 2)
    minutes = digits.substring(2).padStart(2, '0')
  } else {
    hours = digits.substring(0, 2)
    minutes = digits.substring(2, 4)
  }

  // Preserve hours and minutes as entered (up to 99:99)
  const hNum = Math.min(99, Math.max(0, parseInt(hours, 10)))
  const mNum = Math.min(99, Math.max(0, parseInt(minutes, 10)))

  hours = String(hNum).padStart(2, '0')
  minutes = String(mNum).padStart(2, '0')

  return `${hours}:${minutes}`
}

export interface FormValidationRules {
  policyNumber?: string
  contactName?: string
  contactPhone?: string
  registeredVehicleNumber?: string
  accidentDate?: string | null
  accidentTime?: string
  accidentLocation?: string
  accidentDetails?: string
}

export type ValidationErrors = Record<keyof FormValidationRules, string>

/**
 * Validates a single field by key and returns the corresponding error message.
 */
export const validateFnolField = (
  field: keyof FormValidationRules,
  value: string | null | undefined
): string => {
  switch (field) {
    case 'policyNumber':
      if (!value || !value.trim()) return '証券番号を入力してください。'
      break
    case 'contactName':
      if (!value || !value.trim()) return 'ご連絡者のお名前を入力してください。'
      break
    case 'contactPhone':
      if (!value || !value.trim()) return 'ご連絡者様のお電話番号を入力してください。'
      break
    case 'registeredVehicleNumber':
      if (!value || !value.trim()) return '登録番号を入力してください。'
      break
    case 'accidentDate':
      if (!value) return '事故発生年月日を選択してください。'
      break
    case 'accidentTime': {
      if (!value || !value.trim()) return '事故発生時刻を入力してください。'

      // Extract parts whether format is "HH:MM" or "HHMM"
      const rawValue = value.replace(':', '').trim()

      let hours = NaN
      let minutes = NaN

      if (value.includes(':')) {
        const parts = value.split(':')
        hours = parseInt(parts[0], 10)
        minutes = parseInt(parts[1], 10)
      } else if (rawValue.length === 4) {
        hours = parseInt(rawValue.substring(0, 2), 10)
        minutes = parseInt(rawValue.substring(2, 4), 10)
      } else {
        // Fallback split if length is insufficient
        hours = parseInt(value.substring(0, 2), 10)
        minutes = parseInt(value.substring(2), 10)
      }

      // Check hours validation first
      if (isNaN(hours) || hours > 23 || hours < 0) {
        return '0 から 23 までの有効な時間を入力してください 。'
      }

      // Check minutes validation (if hours are valid)
      if (isNaN(minutes) || minutes > 59 || minutes < 0) {
        return '00 から 59 まで有効な分を入力してください 。'
      }

      break
    }
    case 'accidentLocation':
      if (!value || !value.trim()) return '事故場所を入力してください。'
      break
    case 'accidentDetails':
      if (!value || !value.trim()) return '事故状況を入力してください。'
      break
  }
  return ''
}

/**
 * Validates all form fields and returns an object containing error messages.
 */
export const validateFnolForm = (fields: FormValidationRules) => {
  const errors: ValidationErrors = {
    policyNumber: validateFnolField('policyNumber', fields.policyNumber),
    contactName: validateFnolField('contactName', fields.contactName),
    contactPhone: validateFnolField('contactPhone', fields.contactPhone),
    registeredVehicleNumber: validateFnolField('registeredVehicleNumber', fields.registeredVehicleNumber),
    accidentDate: validateFnolField('accidentDate', fields.accidentDate),
    accidentTime: validateFnolField('accidentTime', fields.accidentTime),
    accidentLocation: validateFnolField('accidentLocation', fields.accidentLocation),
    accidentDetails: validateFnolField('accidentDetails', fields.accidentDetails)
  }

  const isValid = !Object.values(errors).some((err) => err !== '')

  return { errors, isValid }
}
export interface SelectOption {
  key: string
  id: number
  description: string
}

/**
 * Maps a selected option key to its corresponding ID and translation key object.
 */
export const toIdPayload = (
  options: SelectOption[],
  selectedKey: string | undefined | null
) => {
  if (!selectedKey) {
    return { id: null, description: '' }
  }

  const found = options.find((opt) => opt.key === selectedKey)
  if (!found) {
    return { id: null, description: '' }
  }

  return {
    id: found.id,
    description: found.description
  }
}
